import React, { useLayoutEffect, useRef } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { format } from "date-fns";

const currencyFields = new Set([
  "invoice_amount",
  "court_fees",
  "mediation_fees",
  "notice_reply_police_comp_fees",
  "professional_fees",
  "clerical_fees",
  "stationary_printing_notary_fees",
  "service_and_postal_fees",
  "summons_and_warrant_fees",
  "outstation_fees",
  "certified_copy_fees",
  "counsel_fees",
  "drafting_fees",
  "scanning_fees",
  "misc_and_service_fees",
  "search_fees",
  "effective_hearing_rate",
  "non_effective_hearing_rate",
  "search_rate",
]);

const numericFields = new Set([
  "invoice_amount",
  "invoice_month",
  "court_fees",
  "mediation_fees",
  "notice_reply_police_comp_fees",
  "professional_fees",
  "clerical_fees",
  "stationary_printing_notary_fees",
  "service_and_postal_fees",
  "summons_and_warrant_fees",
  "outstation_fees",
  "certified_copy_fees",
  "counsel_fees",
  "drafting_fees",
  "scanning_fees",
  "misc_and_service_fees",
  "search_fees",
  "effective_hearing_rate",
  "non_effective_hearing_rate",
  "search_rate",
]);

const monthOptions = Array.from({ length: 12 }, (_, index) => ({
  label: String(index + 1),
  value: String(index + 1),
}));

const sectionCardStyle = {
  background: "#f8fafc",
  border: "1px solid #e5e7eb",
  borderRadius: "18px",
  padding: "1.5rem",
};

const sectionTitleStyle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "#111827",
  marginBottom: "1rem",
};

const fieldLabelStyle = {
  fontSize: "0.82rem",
  fontWeight: 700,
  color: "#374151",
  marginBottom: "0.35rem",
};

const fieldValueStyle = {
  fontSize: "0.95rem",
  color: "#111827",
  minHeight: "1.5rem",
  wordBreak: "break-word",
};

const summaryStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: "18px",
  padding: "1rem 1.15rem",
};

const modalStyles = `
  .invoice-details-dialog {
    max-width: 68vw;
  }

  @media (max-width: 991.98px) {
    .invoice-details-dialog {
      max-width: 92vw;
      margin: 0.5rem auto;
    }
  }

  .invoice-details-content {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    border: 0;
    overflow: hidden;
  }

  .invoice-details-body {
    overflow-y: auto;
    background: #ffffff;
  }

  .invoice-section-card {
    margin-bottom: 1.5rem;
  }

  .invoice-section-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px 22px;
    align-items: start;
  }

  .invoice-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }

  .invoice-field--full {
    grid-column: 1 / -1;
  }

  .invoice-field-label {
    font-size: 0.82rem;
    font-weight: 700;
    color: #374151;
    line-height: 1.25;
    word-break: break-word;
  }

  .invoice-field-value {
    min-height: 1.5rem;
    color: #111827;
    word-break: break-word;
  }

  .invoice-field .form-control,
  .invoice-field .input-group-text {
    min-height: 2.3rem;
  }

  .invoice-field .input-group {
    flex-wrap: nowrap;
  }

  .invoice-details-footer {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: #ffffff;
    border-top: 1px solid #e5e7eb;
  }

  .invoice-details-input,
  .invoice-details-textarea,
  .invoice-details-select {
    border-radius: 12px;
    border-color: #d1d5db;
    box-shadow: none;
    background-color: #ffffff;
    font-size: 0.875rem;
  }

  @media (max-width: 1199.98px) {
    .invoice-section-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 767.98px) {
    .invoice-section-grid {
      grid-template-columns: 1fr;
      gap: 18px;
    }
  }

  .invoice-details-input {
    min-height: calc(1.5em + 0.75rem + 2px);
    padding: 0.25rem 0.65rem;
  }

  .invoice-details-textarea {
    padding: 0.35rem 0.65rem;
    min-height: 72px;
    resize: vertical;
  }

  .invoice-details-textarea--remark {
    min-height: 160px;
  }

  .invoice-details-select {
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }

  .invoice-details-input:focus,
  .invoice-details-textarea:focus,
  .invoice-details-select:focus {
    border-color: #94a3b8;
    box-shadow: 0 0 0 0.15rem rgba(148, 163, 184, 0.15);
  }

  .invoice-details-money-prefix {
    background: #f3f4f6;
    border-color: #d1d5db;
    border-radius: 12px 0 0 12px;
    color: #374151;
    font-weight: 700;
    font-size: 0.875rem;
    padding: 0.25rem 0.6rem;
  }
`;

const normalizeText = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  if (Array.isArray(value)) {
    const list = value
      .map((item) => (typeof item === "string" ? item : item?.label || item?.name || item?.value || String(item || "")))
      .filter(Boolean);
    return list.length ? list.join(", ") : "--";
  }
  return String(value).trim() || "--";
};

const formatRupee = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return normalizeText(value);
  return `₹ ${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(numericValue)}`;
};

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return normalizeText(value);
  return format(date, "dd/MM/yyyy");
};

const formatDateList = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  if (Array.isArray(value)) {
    const items = value.map((item) => normalizeText(item)).filter((item) => item !== "--");
    return items.length ? items.join(", ") : "--";
  }
  if (typeof value === "string") {
    const items = value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    return items.length ? items.join(", ") : "--";
  }
  return normalizeText(value);
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return format(date, "yyyy-MM-dd");
};

const fieldConfig = {
  case: [
    { label: "LAN", name: "lan_number", span: "col-12 col-md-4" },
    { label: "Customer Name", name: "customer_name", span: "col-12 col-md-4" },
    { label: "Type Of Case", name: "type_of_case", span: "col-12 col-md-4" },
    { label: "Client Name", name: "client_name",  span: "col-12 col-md-4" },
    { label: "Branch Name", name: "branch_name", span: "col-12 col-md-4" },
    { label: "Court Name", name: "court_name", span: "col-12 col-md-4" },
    { label: "Effective Dates (i.e dd-mm-yyyy, dd-mm-yyyy)", name: "effective_dates", span: "col-12 col-md-4", isDateList: true },
    { label: "Effective Hearing Rate", name: "effective_hearing_rate", span: "col-12 col-md-4", isCurrency: true },
    { label: "Non Effective Dates (i.e dd-mm-yyyy, dd-mm-yyyy)", name: "non_effective_dates", span: "col-12 col-md-4", isDateList: true },
    { label: "Non Effective Hearing Rate", name: "non_effective_hearing_rate", span: "col-12 col-md-4", isCurrency: true },
    { label: "Status", name: "status", span: "col-12 col-md-4" },
  ],
  billing: [
    { label: "Invoice Amount", name: "invoice_amount", span: "col-12 col-md-4", isCurrency: true },
    { label: "Invoice Number", name: "invoice_number", span: "col-12 col-md-4" },
    { label: "Invoice Date", name: "invoice_date", span: "col-12 col-md-4", isDate: true },
    { label: "Invoice Month", name: "invoice_month", span: "col-12 col-md-4" },
    { label: "Bill To Name", name: "bill_to_name", span: "col-12 col-md-4" },
    { label: "Bill Address", name: "bill_address", span: "col-12", isTextArea: true },
    { label: "Remark", name: "additional_remark", span: "col-12", isTextArea: true },
    { label: "Branch Contact No", name: "branch_contact_no", span: "col-12 col-md-4" },
    { label: "Branch Email Id", name: "branch_email_id", span: "col-12 col-md-4" },
    { label: "Transaction Id", name: "transaction_id", span: "col-12 col-md-4" },
    { label: "Transaction Date", name: "transaction_date", span: "col-12 col-md-4", isDate: true },
  ],
  fees: [
    { label: "Court Fees", name: "court_fees" },
    { label: "Mediation Fees", name: "mediation_fees" },
    { label: "Notice/Reply/Police Comp Fees", name: "notice_reply_police_comp_fees" },
    { label: "Professional Fees", name: "professional_fees" },
    { label: "Clerical Fees", name: "clerical_fees" },
    { label: "Stationary/Printing/Notary Fees", name: "stationary_printing_notary_fees" },
    { label: "Service & Postal Fees", name: "service_and_postal_fees" },
    { label: "Summons & Warrant Fees", name: "summons_and_warrant_fees" },
    { label: "Outstation Fees", name: "outstation_fees" },
    { label: "Certified Copy Fees", name: "certified_copy_fees" },
    { label: "Counsel Fees", name: "counsel_fees" },
    { label: "Drafting Fees", name: "drafting_fees" },
    { label: "Scanning Fees", name: "scanning_fees" },
    { label: "Misc & Service Fees", name: "misc_and_service_fees" },
    { label: "Search Fees", name: "search_fees" },
    { label: "Search Rate", name: "search_rate", defaultValue: 300 },
  ],
};

const SectionCard = ({ title, children }) => (
  <div style={sectionCardStyle} className="invoice-section-card">
    <div style={sectionTitleStyle}>{title}</div>
    {children}
  </div>
);

const AutoGrowTextarea = ({
  value,
  onChange,
  readOnly = false,
  name,
  placeholder,
  className = "",
}) => {
  const textareaRef = useRef(null);

  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    resizeTextarea();
  }, [value, readOnly]);

  return (
    <textarea
      ref={textareaRef}
      name={name}
      value={value ?? ""}
      readOnly={readOnly}
      placeholder={placeholder}
      onChange={onChange}
      onInput={resizeTextarea}
      className={className}
    />
  );
};

const FieldCell = ({
  label,
  value,
  editing,
  name,
  isCurrency,
  isDate,
  isDateList,
  isTextArea,
  onFieldChange,
}) => {
  const displayValue = isCurrency
    ? formatRupee(value)
    : isDate
      ? formatDate(value)
      : isDateList
        ? formatDateList(value)
        : normalizeText(value);

  if (!editing) {
    if (name === "remark") {
      return (
        <div className={`invoice-field ${isTextArea ? "invoice-field--full" : ""}`}>
          <div className="invoice-field-label">{label}</div>
          <Form.Control
            as="textarea"
            rows={4}
            value={value ?? ""}
            readOnly
            className="invoice-details-textarea"
          />
        </div>
      );
    }
    return (
      <div className={`invoice-field ${isTextArea ? "invoice-field--full" : ""}`}>
        <div className="invoice-field-label">{label}</div>
        <div className="invoice-field-value" style={fieldValueStyle}>{displayValue}</div>
      </div>
    );
  }

  if (isTextArea) {
    return (
      <div className="invoice-field invoice-field--full">
        <div className="invoice-field-label">{label}</div>
        <AutoGrowTextarea
          name={name}
          value={value}
          onChange={onFieldChange}
          placeholder={`Enter ${label.toLowerCase()}`}
          className="invoice-details-textarea invoice-details-textarea--remark"
        />
      </div>
    );
  }

  if (name === "invoice_month") {
    return (
      <div className="invoice-field">
        <div className="invoice-field-label">{label}</div>
        <Form.Select
          name={name}
          value={value ?? ""}
          onChange={onFieldChange}
          className="invoice-details-select"
        >
          <option value="">Select month</option>
          {monthOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      </div>
    );
  }

  if (isCurrency || numericFields.has(name)) {
    return (
      <div className="invoice-field">
        <div className="invoice-field-label">{label}</div>
        <Form.Group>
          <div className="input-group">
            <span className="input-group-text invoice-details-money-prefix">₹</span>
            <Form.Control
              type="number"
              step="1"
              min="0"
              inputMode="numeric"
              name={name}
              value={value ?? ""}
              onChange={onFieldChange}
              className="invoice-details-input"
              placeholder={`Enter ${label.toLowerCase()}`}
            />
          </div>
        </Form.Group>
      </div>
    );
  }

  if (isDate) {
    return (
      <div className="invoice-field">
        <div className="invoice-field-label">{label}</div>
        <Form.Control
          type="date"
          name={name}
          value={toDateInputValue(value)}
          onChange={onFieldChange}
          className="invoice-details-input"
        />
      </div>
    );
  }

  return (
    <div className="invoice-field">
      <div className="invoice-field-label">{label}</div>
      <Form.Control
        type={numericFields.has(name) ? "number" : "text"}
        step={numericFields.has(name) ? "1" : undefined}
        min={numericFields.has(name) ? "0" : undefined}
        inputMode={numericFields.has(name) ? "numeric" : undefined}
        name={name}
        value={value ?? ""}
        onChange={onFieldChange}
        className="invoice-details-input"
        placeholder={`Enter ${label.toLowerCase()}`}
      />
    </div>
  );
};

const InvoiceDetailsModal = ({
  show,
  mode,
  invoice,
  formData,
  clientOptions = [],
  branchOptions = [],
  clientsLoading = false,
  branchesLoading = false,
  clientNameReadOnly = false,
  branchNameReadOnly = false,
  onClose,
  onEditRequest,
  onSave,
  onFieldChange,
  saving,
}) => {
  const adding = mode === "add";
  const editing = mode === "edit" || adding;
  const source = editing ? formData || {} : invoice || {};
  const modalTitle = adding ? "Add Invoice" : editing ? "Edit Invoice Details" : "View Invoice Details";
  // Prefer showing client_name in header, fall back to client id or customer_name
  const headerName = normalizeText(source?.client_name || source?.client || source?.customer_name || invoice?.client_name || invoice?.client || invoice?.customer_name);
  const headerLan = normalizeText(source?.lan_number || invoice?.lan_number || source?.lan || invoice?.lan);
  const headerText = `${headerName}${headerLan !== "--" ? ` (${headerLan})` : ""}`;

  const getValue = (config) => {
    if (config.fallbackNames) {
      for (const fallbackName of config.fallbackNames) {
        const fallbackValue = source?.[fallbackName];
        if (fallbackValue !== null && fallbackValue !== undefined && fallbackValue !== "") {
          return fallbackValue;
        }
      }
    }
    const val = source?.[config.name];
    if ((val === null || val === undefined || val === "") && adding && config.defaultValue !== undefined) {
      return config.defaultValue;
    }
    return val;
  };

  const handleNumericChange = (event) => {
    const { name, value } = event.target;
    const sanitizedValue = numericFields.has(name) ? value.replace(/[^0-9]/g, "") : value;
    onFieldChange(name, sanitizedValue);
  };

  const handleTextChange = (event) => {
    const { name, value } = event.target;
    onFieldChange(name, value);
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop
      keyboard
      dialogClassName="invoice-details-dialog"
      contentClassName="invoice-details-content"
      backdropClassName="invoice-details-backdrop"
    >
      <style>{modalStyles}</style>
      <Modal.Header closeButton className="border-0 pb-0 px-4 pt-4">
        <div>
          <Modal.Title className="fs-4 fw-bold mb-2">{modalTitle}</Modal.Title>
          <div className="fs-6 fw-semibold text-dark">{adding ? "New Invoice" : headerText}</div>
        </div>
      </Modal.Header>

      <Modal.Body className="invoice-details-body px-4 py-3">
        <SectionCard title="Case Details">
          <div className="invoice-section-grid">
            {fieldConfig.case.map((config) => {
              if (editing && config.name === "client_name") {
                const clientDisplayValue = normalizeText(
                  source?.client_name || source?.client || source?.customer_name || "-"
                );

                if (clientNameReadOnly) {
                  return (
                    <div className="invoice-field" key={config.name}>
                      <div className="invoice-field-label">{config.label}</div>
                      <Form.Control
                        type="text"
                        value={clientDisplayValue === "--" ? "-" : clientDisplayValue}
                        readOnly
                        className="invoice-details-input"
                      />
                    </div>
                  );
                }

                const selectedClientId =
                  source?.client_id ||
                  (typeof source?.client === "number" || /^[0-9]+$/.test(String(source?.client || ""))
                    ? source?.client
                    : "");

                return (
                  <div className="invoice-field" key={config.name}>
                    <div className="invoice-field-label">{config.label}</div>
                    <Form.Select
                      name="client_id"
                      value={selectedClientId || ""}
                      onChange={handleTextChange}
                      className="invoice-details-select"
                      disabled={clientsLoading}
                    >
                      <option value="">{clientsLoading ? "Loading clients..." : "Select client"}</option>
                      {clientOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                );
              }

              if (editing && config.name === "branch_name") {
                const selectedClientId =
                  source?.client_id ||
                  (typeof source?.client === "number" || /^[0-9]+$/.test(String(source?.client || ""))
                    ? source?.client
                    : "");

                const branchDisplayValue = normalizeText(source?.branch_name || source?.branch || source?.batch?.branch_name || source?.branchName || "-");

                if (branchNameReadOnly) {
                  return (
                    <div className="invoice-field" key={config.name}>
                      <div className="invoice-field-label">{config.label}</div>
                      <Form.Control
                        type="text"
                        value={branchDisplayValue === "--" ? "-" : branchDisplayValue}
                        readOnly
                        className="invoice-details-input"
                      />
                    </div>
                  );
                }

                return (
                  <div className="invoice-field" key={config.name}>
                    <div className="invoice-field-label">{config.label}</div>
                    <Form.Select
                      name="branch_name"
                      value={source?.branch_name || ""}
                      onChange={handleTextChange}
                      className="invoice-details-select"
                      disabled={branchesLoading || !selectedClientId}
                    >
                      <option value="">{branchesLoading ? "Loading branches..." : "Select branch"}</option>
                      {branchOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                );
              }

              return (
                <FieldCell
                  key={config.name}
                  label={config.label}
                  value={getValue(config)}
                  editing={editing}
                  name={config.name}
                  isCurrency={config.isCurrency}
                  isDate={config.isDate}
                  isDateList={config.isDateList}
                  onFieldChange={config.isCurrency || numericFields.has(config.name) ? handleNumericChange : handleTextChange}
                />
              );
            })}
          </div>
        </SectionCard>

        <SectionCard title="Billing Details">
          <div className="invoice-section-grid">
            {fieldConfig.billing.map((config) => (
              <FieldCell
                key={config.name}
                label={config.label}
                value={getValue(config)}
                editing={editing}
                name={config.name}
                isCurrency={config.isCurrency}
                isDate={config.isDate}
                isTextArea={config.isTextArea}
                onFieldChange={config.isCurrency || numericFields.has(config.name) ? handleNumericChange : handleTextChange}
              />
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Fee Details">
          <div className="invoice-section-grid">
            {fieldConfig.fees.map((config) => (
              <FieldCell
                key={config.name}
                label={config.label}
                value={getValue(config)}
                editing={editing}
                name={config.name}
                isCurrency
                onFieldChange={handleNumericChange}
              />
            ))}
          </div>
        </SectionCard>
      </Modal.Body>

      <Modal.Footer className="invoice-details-footer px-4 py-3">
        <Button variant="outline-secondary" onClick={onClose}>
          Close
        </Button>
        {editing ? (
          <Button
            variant="primary"
            onClick={onSave}
            disabled={saving || (mode === "edit" && !source?.id)}
          >
            {saving ? (adding ? "Creating..." : "Saving...") : adding ? "Create Invoice" : "Save Changes"}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
};

export default InvoiceDetailsModal;
